/**
 * Homepage "Where's good right now?" sport choice: All, Ski and Surf chips,
 * then a More sports menu. Client-safe.
 */

/** Sports with homepage spot cards computed from the tracker data. */
export type HomeCardToggle = "all" | "ski" | "surf" | "mtb" | "wing-foil";
/** Menu sports without cards: one card links to their leaderboard. */
export type HomeLinkOnlySport = "wind" | "bodyboarding" | "sup" | "sailing" | "diving" | "open-water";
export type HomeSportToggle = HomeCardToggle | HomeLinkOnlySport;

export const HOME_PRIMARY_CHIPS: ReadonlyArray<{ key: "all" | "ski" | "surf"; label: string }> = [
  { key: "all", label: "All" },
  { key: "ski", label: "Ski" },
  { key: "surf", label: "Surf" },
];

export type HomeMoreSport = {
  key: "mtb" | "wing-foil" | HomeLinkOnlySport;
  /** Menu item text. */
  label: string;
  /** Short text that replaces "More sports" on the chip once chosen. */
  chip: string;
  /** In-sentence name. */
  lower: string;
  /** Leaderboard: /conditions/{key}. */
  href: string;
  hasCards: boolean;
};

export const HOME_MORE_SPORTS: readonly HomeMoreSport[] = [
  { key: "mtb", label: "MTB", chip: "MTB", lower: "MTB", href: "/conditions/mtb", hasCards: true },
  { key: "wing-foil", label: "Wing foil", chip: "Wing foil", lower: "wing foil", href: "/conditions/wing-foil", hasCards: true },
  { key: "wind", label: "Kite and windsurf", chip: "Kite/windsurf", lower: "kite and windsurf", href: "/conditions/wind", hasCards: false },
  { key: "bodyboarding", label: "Bodyboarding", chip: "Bodyboarding", lower: "bodyboarding", href: "/conditions/bodyboarding", hasCards: false },
  { key: "sup", label: "SUP", chip: "SUP", lower: "SUP", href: "/conditions/sup", hasCards: false },
  { key: "sailing", label: "Sailing", chip: "Sailing", lower: "sailing", href: "/conditions/sailing", hasCards: false },
  { key: "diving", label: "Diving", chip: "Diving", lower: "diving", href: "/conditions/diving", hasCards: false },
  { key: "open-water", label: "Open-water swimming", chip: "Open water", lower: "open-water swimming", href: "/conditions/open-water", hasCards: false },
];

export function moreSport(sport: HomeSportToggle): HomeMoreSport | null {
  return HOME_MORE_SPORTS.find((entry) => entry.key === sport) ?? null;
}

/** The link under the cards for the chosen sport. */
export function homeBoardLink(sport: HomeSportToggle, skiOffSeason: boolean): { href: string; label: string } {
  if (sport === "all") return { href: "/conditions", label: "See all conditions" };
  if (sport === "ski") {
    return skiOffSeason
      ? { href: "/conditions", label: "See all conditions" }
      : { href: "/conditions/piste", label: "See all ski spots" };
  }
  if (sport === "surf") return { href: "/conditions/surf", label: "See all surf spots" };
  const entry = moreSport(sport);
  if (!entry || !entry.hasCards) return { href: "/conditions", label: "See all conditions" };
  return { href: entry.href, label: `See all ${entry.lower} spots` };
}

/** Polite live-region text after a sport is chosen. */
export function homeSportMessage(sport: HomeSportToggle, skiOffSeason: boolean): string {
  if (sport === "all") return "Showing top spots for all sports";
  if (sport === "ski") {
    return skiOffSeason ? "Ski is off-season. Scores switch on from late November." : "Showing top spots for ski";
  }
  if (sport === "surf") return "Showing top spots for surf";
  const entry = moreSport(sport);
  if (!entry) return "";
  return entry.hasCards ? `Showing top spots for ${entry.lower}` : `Showing the ${entry.lower} leaderboard link`;
}
